import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, QRCode, Tag } from 'antd';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import citationService from 'common/services/citation-service/citation.service';
import { ICitationRead } from 'common/services/citation-service/citation.service.types';
import configurationStore from 'common/stores/configuration/configuration.store';
import { notification } from 'common/utils/notification';
import { CitationStatus, citationStatusColorMap, citationStatusMap } from 'features/eiq-manage/enums/citation-status.enum';
import CitationActionButtons from '../citation-actions/citation-action-buttons';
import EditCitationModal from '../edit-citation-modal/edit-citation-modal';
import { hasActivePenalty } from '../helpers/citation-utils';
import { useCitationModals } from '../hooks/use-citation-modals';
import MoveCitationModal from '../move-citation-modal/move-citation-modal';
import CitationDetailsContent from './citation-details-content';
import { trackPromise } from 'react-promise-tracker';
import { pdf } from '@react-pdf/renderer';
import { getLegalNotes, printPdf } from '../helpers/citations-helper';
import { PrintCitationDetails } from '../print-citation-details/print-citation-details';
import { getFile } from 'common/helpers/image.helper';
import { UploadFileTypes } from 'common/services/file-service/file.service.types';
import { IPrintCitaitonDetailsData } from '../print-citation-details/print-citation-details.types';
import './citation-details.scss';
import { saveAs } from 'file-saver';
import featureStore from 'common/stores/features/feature.store';

const CitationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fetchRequestIdRef = useRef(0);
  const [citation, setCitation] = useState<ICitationRead | null>(null);
  const citationConfiguration = configurationStore.configuration?.citations;
  const citationPaymentLinkQR = useRef<HTMLDivElement>(null);

  const refreshCitation = useCallback(async () => {
    const requestId = ++fetchRequestIdRef.current;

    if (!id) {
      setCitation(null);
      return;
    }

    const result = await citationService.getByExternalId(id);
    if (requestId === fetchRequestIdRef.current) {
      setCitation(result);
    }
  }, [id]);

  useEffect(() => {
    void refreshCitation();

    return () => {
      fetchRequestIdRef.current += 1;
    };
  }, [refreshCitation]);

  const { moveCitationModal, editCitationModal } = useCitationModals(citation, refreshCitation);

  const executeCitationAction = useCallback(
    async (action: (citationItemId: number, residentId: number) => Promise<unknown>) => {
      if (!citation) {
        return;
      }

      await action(citation.id, citation.residentId!);
      await refreshCitation();
    },
    [citation, refreshCitation],
  );

  const handleConfirm = () => executeCitationAction((citationId, residentId) => citationService.confirm(citationId, residentId));
  const handleApprove = () => executeCitationAction((citationId, residentId) => citationService.approve(citationId, residentId));
  const handleConfirmPayment = () =>
    executeCitationAction((citationId, residentId) => citationService.confirmPayment(citationId, residentId));

  const handleWaive = async () => {
    if (!citation) {
      return;
    }

    const shouldShowSuspensionMessage = hasActivePenalty(citation.status);
    await executeCitationAction((citationId, residentId) => citationService.waive(citationId, residentId));

    if (shouldShowSuspensionMessage) {
      notification.success({ message: 'Citation and the related suspension were waived.', duration: 5 });
    }
  };

  const handleCancel = async () => {
    if (!citation) {
      return;
    }

    const shouldShowSuspensionMessage = hasActivePenalty(citation.status);
    await executeCitationAction((citationId, residentId) => citationService.cancel(citationId, residentId));

    if (shouldShowSuspensionMessage) {
      notification.success({ message: 'Citation and the related suspension were cancelled.', duration: 5 });
    }
  };

  const status = citation?.status as CitationStatus | undefined;

  const getQrImage = () => {
    if (!citation?.paymentLinkUrl) {
      return null;
    }

    const canvas = citationPaymentLinkQR.current?.querySelector<HTMLCanvasElement>('canvas');
    return canvas?.toDataURL('image/png');
  };

  const buildPrintCitationDetailsPdfProps = async (row: ICitationRead): Promise<IPrintCitaitonDetailsData> => {
    const imagesUrls = [...(row.photosUrl ?? []), ...(row.licensePlatePhotosUrl ?? [])];

    let evidenceImagesSrc: string[] = [];

    try {
      evidenceImagesSrc = await Promise.all(imagesUrls.map((url) => getFile(url, UploadFileTypes.Citation)));
    } catch (e) {
      console.error(e);
    }

    const paymentLinkQRSrc = getQrImage();

    return {
      ...row,
      evidenceImagesSrc,
      paymentLinkQRSrc,
    } as IPrintCitaitonDetailsData;
  };

  const createCitationDetailsPdf = async (): Promise<Blob> => {
    if (!citation) {
      throw new Error('Citation is not loaded');
    }

    const [communityLogo, data] = await Promise.all([
      configurationStore.getCommunityLogoForPDF(),
      buildPrintCitationDetailsPdfProps(citation),
    ]);

    const communityName = configurationStore.configuration?.default.communityName ?? '';
    const isAutoIssueInvoice = citationConfiguration?.isAutoIssueInvoice ?? false;
    const citationGeneralInfo = citationConfiguration?.generalInformation;
    const legalNotes = getLegalNotes(citationConfiguration?.rules ?? [], citation.sequenceNumber ?? 1);

    const document = (
      <PrintCitationDetails
        communityLogo={communityLogo}
        data={data}
        communityName={communityName}
        isAutoIssueInvoice={isAutoIssueInvoice}
        citationGeneralInfo={citationGeneralInfo}
        legalNotes={legalNotes}
      />
    );

    return pdf(document).toBlob();
  };

  const handlePrint = async (): Promise<void> => {
    await trackPromise(createCitationDetailsPdf().then(printPdf));
  };

  const handleExportPdf = async (): Promise<void> => {
    if (!citation) {
      return;
    }

    const externalId = citation.externalId;

    await trackPromise(
      createCitationDetailsPdf().then((blob) => {
        saveAs(blob, `IntelliTicket-${externalId}.pdf`);
      }),
    );
  };

  const handleSuspendTransponders = async () => {
    if (!citation) {
      return;
    }

    await executeCitationAction((citationId, residentId) => citationService.resume(citationId, residentId));
    notification.success({ message: 'Transponders suspended.', duration: 5 });
  };

  const handleUnsuspendTransponders = async () => {
    if (!citation) {
      return;
    }

    await executeCitationAction((citationId, residentId) => citationService.pause(citationId, residentId));
    notification.success({ message: 'Transponders resumed.', duration: 5 });
  };

  return (
    <>
      {citation?.paymentLinkUrl && (
        <div ref={citationPaymentLinkQR} style={{ position: 'absolute', left: -9999 }}>
          <QRCode value={String(citation?.paymentLinkUrl)} type="canvas" size={200} />
        </div>
      )}

      <Card className="eiq-card eiq-card-border full-card citation-details">
        <div className="citation-title">
          <span className="citation-number">Citation #{citation?.externalId ?? ''}</span>
          {status !== undefined && (
            <Tag className="eiq-tag" color={citationStatusColorMap[status]} style={{ color: '#292B2E' }}>
              {citationStatusMap[status]}
            </Tag>
          )}
        </div>

        <div className="buttons">
          <Button onClick={() => navigate('/eiq-manage/citations')}>Back</Button>
          <CitationActionButtons
            citation={citation}
            isConfirmPaymentEnabled={featureStore.isEIQIntelliTicketConfirmPaymentEnabled}
            onPrint={handlePrint}
            onEdit={editCitationModal.showModal}
            onMove={moveCitationModal.showModal}
            onConfirm={handleConfirm}
            onApprove={handleApprove}
            onConfirmPayment={handleConfirmPayment}
            onWaive={handleWaive}
            onCancel={handleCancel}
            onExportPdf={handleExportPdf}
            onSuspendTransponders={handleSuspendTransponders}
            onUnsuspendTransponders={handleUnsuspendTransponders}
          />
        </div>

        {citation && <CitationDetailsContent citation={citation} />}
      </Card>

      <MoveCitationModal
        initData={moveCitationModal.initData}
        title="Move citation"
        isOpen={moveCitationModal.isOpenModal}
        onClose={moveCitationModal.hideModal}
      />
      <EditCitationModal
        initData={editCitationModal.initData}
        title="Edit License Plate Number and Type"
        isOpen={editCitationModal.isOpenModal}
        onClose={editCitationModal.hideModal}
      />
    </>
  );
};

export default observer(CitationDetails);
